
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Copy, Mail, User, Building, Calendar, MessageSquare } from 'lucide-react';

interface ViewFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: any;
}

const ViewFeedbackDialog: React.FC<ViewFeedbackDialogProps> = ({
  isOpen,
  onClose,
  feedback
}) => {
  const [feedbackDetails, setFeedbackDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && feedback) {
      fetchFeedbackDetails();
    }
  }, [isOpen, feedback]);

  const fetchFeedbackDetails = async () => {
    setLoading(true);
    try {
      // First check if we need to fetch the associated feedback (if it exists)
      if (feedback.status === 'completed' && feedback.feedback_id) {
        const { data, error } = await supabase
          .from('feedbacks')
          .select('*')
          .eq('id', feedback.feedback_id)
          .single();
        
        if (error) throw error;
        
        setFeedbackDetails({
          ...feedback,
          feedbackContent: data
        });
      } else {
        setFeedbackDetails(feedback);
      }
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les détails du feedback.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'completed':
        return <Badge className="bg-green-500">Répondu</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500">En attente</Badge>;
      case 'expired':
        return <Badge className="bg-red-500">Expiré</Badge>;
      case 'canceled':
        return <Badge className="bg-gray-500">Annulé</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copié !",
      description: "Le texte a été copié dans le presse-papiers."
    });
  };

  if (!feedbackDetails) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
            Détails de la demande de feedback
          </DialogTitle>
          <DialogDescription>
            ID: {feedbackDetails.id}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
          </div>
        ) : (
          <Tabs defaultValue="request" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="request">Demande</TabsTrigger>
              {feedbackDetails.status === 'completed' && (
                <TabsTrigger value="response">Réponse</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="request">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Candidat</h3>
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        {getInitials(feedbackDetails.candidate_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{feedbackDetails.candidate_name}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Mail className="h-3 w-3 mr-1" />
                        {feedbackDetails.candidate_email}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-5 w-5 ml-1"
                          onClick={() => copyToClipboard(feedbackDetails.candidate_email)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Recruteur</h3>
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-purple-100 text-purple-600">
                        {getInitials(feedbackDetails.recruiter_name || 'Recruteur')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{feedbackDetails.recruiter_name || 'Non spécifié'}</p>
                      {feedbackDetails.recruiter_email && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Mail className="h-3 w-3 mr-1" />
                          {feedbackDetails.recruiter_email}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5 ml-1"
                            onClick={() => copyToClipboard(feedbackDetails.recruiter_email)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {feedbackDetails.recruiter_company && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Building className="h-3 w-3 mr-1" />
                          {feedbackDetails.recruiter_company}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Informations</h3>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <User className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="font-medium mr-2">Poste :</span> 
                      {feedbackDetails.job_title || 'Non spécifié'}
                    </div>
                    <div className="flex items-center text-sm">
                      <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                      <span className="font-medium mr-2">Créé le :</span> 
                      {new Date(feedbackDetails.created_at).toLocaleDateString('fr-FR')}
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="font-medium mr-2">Statut :</span> 
                      {getStatusBadge(feedbackDetails.status)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="font-medium text-lg mb-2">Message</h3>
                <div className="p-4 bg-gray-50 rounded-md whitespace-pre-wrap">
                  {feedbackDetails.message || 'Aucun message fourni.'}
                </div>
              </div>
            </TabsContent>
            
            {feedbackDetails.status === 'completed' && (
              <TabsContent value="response">
                <div className="space-y-6">
                  {feedbackDetails.feedbackContent ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="font-medium text-lg mb-2">Points positifs</h3>
                          <div className="p-4 bg-green-50 rounded-md whitespace-pre-wrap">
                            {feedbackDetails.feedbackContent.positives || 'Aucun point positif mentionné.'}
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="font-medium text-lg mb-2">Axes d&apos;amélioration</h3>
                          <div className="p-4 bg-amber-50 rounded-md whitespace-pre-wrap">
                            {feedbackDetails.feedbackContent.improvements || 'Aucun axe d&apos;amélioration mentionné.'}
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="font-medium text-lg mb-2">Commentaire général</h3>
                        <div className="p-4 bg-gray-50 rounded-md whitespace-pre-wrap">
                          {feedbackDetails.feedbackContent.general_comment || 'Aucun commentaire général fourni.'}
                        </div>
                      </div>
                      
                      {feedbackDetails.feedbackContent.final_message && (
                        <div>
                          <h3 className="font-medium text-lg mb-2">Message final</h3>
                          <div className="p-4 bg-blue-50 rounded-md whitespace-pre-wrap">
                            {feedbackDetails.feedbackContent.final_message}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Réponse indisponible ou supprimée.
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewFeedbackDialog;
